import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Toast } from 'ngx-toastr';

/**
 * The toast ngx-toastr renders, with one difference: it renders itself instead
 * of waiting for the application's change detection.
 *
 * A toast is created by `createComponent` and attached to `ApplicationRef`
 * last, so its first render belongs to the next tick — and the app is zoneless,
 * so that tick is the only chance it gets. If anything else throws while that
 * tick is running, the tick unwinds and every view it had not reached yet keeps
 * its dirty flag with nobody left to schedule another pass. The toast then sits
 * in the DOM as `<div toast-component>` with four empty `@if` anchors, 0x0,
 * forever — measured on `/transactions/transfer/<unknown id>`, where a
 * memoized NgRx selector threw `Cannot read properties of undefined (reading
 * 'find')` from inside a `computed` during the same tick that should have
 * rendered the toast carrying the server's message.
 *
 * The message is the one thing that must survive a broken view, because a
 * broken view is what it is usually reporting. So this component checks its own
 * view: `detectChanges()` on a detached-from-nobody root view renders the toast
 * and its host bindings regardless of what the rest of the tree is doing. It is
 * scheduled as a microtask rather than called inline, so it cannot re-enter a
 * change detection pass that `ToastrService.show()` was called from.
 *
 * Everything else is the library's: the timers run on `TimeoutsService`, tap
 * and hover come from host listeners, and removal is a DOM detach — none of it
 * needs change detection, which is why only rendering had to be fixed. All of
 * it was measured on a live toast: tap dismisses in 304 ms, hover holds it past
 * its 5 s timeout, `mouseleave` lets it go 1.3 s later, and a starved toast
 * still leaves after 5.3 s.
 *
 * The host is `<app-toast>` rather than the library's `<div toast-component>`
 * — the toastr stylesheet sizes the toast through its classes, so the tag only
 * costs the `display: block` below. The one thing lost when the app is starved
 * is the fade-in: `animate.enter` needs a render pass, so the toast appears at
 * once instead of over 300 ms.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-toast',
  host: {
    'animate.enter': 'toast-in',
    '[style.--animation-easing]': 'params.easing',
    '[style.--animation-duration]': 'params.easeTime + "ms"',
  },
  template: `
    @let toastOptions = options();

    @if (toastOptions.closeButton) {
      <button
        type="button"
        class="toast-close-button"
        aria-label="Close"
        (click)="remove()"
      >
        <span aria-hidden="true">&times;</span>
      </button>
    }

    @if (title()) {
      <div [class]="toastOptions.titleClass" [attr.aria-label]="title()">
        {{ title() }}

        @if (duplicatesCount) {
          <ng-container>[{{ duplicatesCount + 1 }}]</ng-container>
        }
      </div>
    }

    @if (message()) {
      @if (toastOptions.enableHtml) {
        <div
          role="alert"
          [class]="toastOptions.messageClass"
          [innerHTML]="message()"
        ></div>
      } @else {
        <div
          role="alert"
          [class]="toastOptions.messageClass"
          [attr.aria-label]="message()"
        >
          {{ message() }}
        </div>
      }
    }

    @if (toastOptions.progressBar) {
      <div>
        <div class="toast-progress" [style.width]="width() + '%'"></div>
      </div>
    }
  `,
  styles: [
    `
      /* The library's own toast is a <div>; ours is an element, and the toastr
         stylesheet sizes it through the .ngx-toastr class either way. */
      :host {
        display: block;
      }

      :host.toast-in {
        animation: app-toast-fade var(--animation-duration)
          var(--animation-easing);
      }

      :host.toast-out {
        animation: app-toast-fade var(--animation-duration)
          var(--animation-easing) reverse forwards;
      }

      @keyframes app-toast-fade {
        0% {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
    `,
  ],
})
export class AppToastComponent extends Toast {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly hostRef = inject(ElementRef<HTMLElement>);

  private renderQueued = false;
  private destroyed = false;

  constructor() {
    super();

    this.renderLocally();

    // The three things that change what a toast shows after it is built. The
    // library reacts to them by writing signals, which is enough in a healthy
    // app and nothing at all in a starved one.
    const refresh = () => this.renderLocally();
    const toastRef = this.toastPackage.toastRef;

    toastRef.afterActivate().pipe(takeUntilDestroyed()).subscribe(refresh);
    toastRef.countDuplicate().pipe(takeUntilDestroyed()).subscribe(refresh);
    toastRef.timeoutReset().pipe(takeUntilDestroyed()).subscribe(refresh);
  }

  public override ngOnDestroy(): void {
    this.destroyed = true;
    super.ngOnDestroy();
  }

  private renderLocally(): void {
    if (this.destroyed || this.renderQueued) return;
    this.renderQueued = true;

    queueMicrotask(() => {
      this.renderQueued = false;
      if (this.destroyed) return;

      this.applyHostState();
      this.cdr.detectChanges();
    });
  }

  /**
   * `detectChanges()` refreshes this component's own view. The host bindings
   * that carry the toast's classes and animation variables belong to the view
   * that *holds* the host element — the root view `createComponent` made — and
   * that one is only ever refreshed by the application's tick. Without this the
   * message renders as bare text: measured 166x24 instead of 380x54, no
   * `toast-error` background, no fade. So the same values are written here,
   * from the same signals the bindings read.
   */
  private applyHostState(): void {
    const host = this.hostRef.nativeElement;

    for (const className of this.toastClasses().split(' ')) {
      if (className) host.classList.add(className);
    }

    const display = this.displayStyle();
    if (display) {
      host.style.display = display;
    } else {
      host.style.removeProperty('display');
    }

    host.style.setProperty('--animation-easing', String(this.params.easing));
    host.style.setProperty('--animation-duration', `${this.params.easeTime}ms`);
  }
}
